import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PendingActions as PendingIcon } from "@mui/icons-material";
import type { PendingReviewItem } from "@/services/vendorProfileService";
import { formatWaiting } from "./utils/formatWaiting";

type Props = {
  items: PendingReviewItem[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  onOpen: (taskId: string) => void;
};

function typeLabel(item: PendingReviewItem): string {
  if (item.source === "CHANGE_REQUEST") {
    return item.code ? `Change request · ${item.code}` : "Change request";
  }
  return "Onboarding";
}

/**
 * "Pending with you" — the signed-in reviewer's open onboarding and
 * change-request tasks with the stage each waits at and how long it has waited
 * since the current assignment (ENG-27734). Rendered below the onboarded-vendors
 * list in the internal view only; vendors never reach this component.
 */
export function PendingReviewsCard({ items, isLoading, isError, onOpen }: Props) {
  const rows = items ?? [];
  return (
    <Paper
      variant="outlined"
      data-testid="pending-reviews-card"
      sx={{
        mt: 3,
        borderColor: "#E0E4EF",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 4,
          py: 3,
          borderBottom: 1,
          borderColor: "#E0E4EF",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            bgcolor: "primary.light",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PendingIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            Pending with you
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Onboarding and change requests waiting for your review, oldest first.
          </Typography>
        </Box>
        {!isLoading && !isError && rows.length > 0 && (
          <Chip size="small" label={rows.length} sx={{ fontWeight: 700 }} />
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Couldn&apos;t load your pending reviews. Please refresh to retry.
          </Alert>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary" }}>
            Nothing is waiting for your review.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Waiting</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => (
                <TableRow key={item.task_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {item.reference || item.task_visible_id || "—"}
                    {item.changed_fields && item.changed_fields.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: "text.secondary" }}
                      >
                        {item.changed_fields.join(", ")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{typeLabel(item)}</TableCell>
                  <TableCell>{item.stage_label || item.stage}</TableCell>
                  <TableCell>
                    {formatWaiting(item.assigned_at, item.waiting_seconds)}
                  </TableCell>
                  <TableCell>
                    {item.flags && item.flags.length > 0 ? (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {item.flags.map((f, i) => (
                          <Chip
                            key={`${f.type}-${f.status}-${i}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${f.type}: ${f.status}`}
                          />
                        ))}
                      </Box>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onOpen(item.task_id)}
                      aria-label={`Open review for ${item.reference || item.task_id}`}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export default PendingReviewsCard;
